import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BookOpen, ArrowRight } from "lucide-react";

/**
 * Related blog articles for a given product or resource slug.
 * Used on ProductDetail + ResourceDetail pages for internal linking (SEO + engagement).
 */
export default function RelatedArticles({ productSlug, resourceSlug, limit = 3, headline }) {
  const [items, setItems] = useState([]);
  const slug = productSlug || resourceSlug;
  const endpoint = resourceSlug
    ? `/resources/${resourceSlug}/related-articles`
    : `/products/${productSlug}/related-articles`;

  useEffect(() => {
    if (!slug) return;
    api
      .get(`${endpoint}?limit=${limit}`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, [slug, endpoint, limit]);

  if (!items.length) return null;

  const h2 = headline || (resourceSlug
    ? "Articles du blog liés à cette ressource"
    : "Articles du blog liés à ce matériel");

  return (
    <section
      className="py-12 border-t border-ink/10"
      data-testid="related-articles-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-3">
          Pour approfondir
        </p>
        <h2 className="font-display text-2xl md:text-3xl mb-8">
          {h2}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {items.map((a) => (
            <Link
              key={a.slug}
              to={`/blog/${a.slug}`}
              data-testid={`related-article-${a.slug}`}
              className="group block bg-white rounded-2xl overflow-hidden border border-ink/10 hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <div className="aspect-[16/9] bg-gradient-to-br from-pedablue/15 to-sage/25 relative overflow-hidden">
                {a.cover_image ? (
                  <img
                    src={a.cover_image}
                    alt={a.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <BookOpen size={28} className="text-pedablue/40" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-display text-lg leading-tight mb-2 group-hover:text-pedablue transition line-clamp-2">
                  {a.title}
                </h3>
                {a.excerpt && (
                  <p className="text-xs text-ink-soft line-clamp-2 mb-3">{a.excerpt}</p>
                )}
                <span className="inline-flex items-center gap-1 text-xs text-pedablue">
                  Lire l&apos;article
                  <ArrowRight size={12} className="group-hover:translate-x-0.5 transition" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
